/**
 * Project Context
 * Global state management for design projects
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import { useSocket } from './SocketContext';

const ProjectContext = createContext();

export function useProjects() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within ProjectProvider');
  }
  return context;
}

export function ProjectProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    workflowStatus: '',
    currentPhase: '',
    sidId: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const socket = useSocket();

  /**
   * Fetch all projects with filters
   */
  const fetchProjects = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await api.get('/design-projects', { params });

      setProjects(response.data.designProjects || []);
      setPagination({
        page: response.data.pagination?.page || 1,
        limit: response.data.pagination?.limit || 20,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 0
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  /**
   * Fetch my assigned projects
   */
  const fetchMyProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/design-projects/my');
      setProjects(response.data.designProjects || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch my projects');
      console.error('Error fetching my projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch project by ID
   */
  const fetchProjectById = useCallback(async (projectId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/design-projects/${projectId}`);
      const project = response.data.designProject;

      setSelectedProject(project);

      // Update in projects list if exists
      setProjects(prev => {
        const index = prev.findIndex(p => p.id === projectId);
        if (index !== -1) {
          const newProjects = [...prev];
          newProjects[index] = project;
          return newProjects;
        }
        return prev;
      });

      return project;
    } catch (err) {
      setError(err.message || 'Failed to fetch project');
      console.error('Error fetching project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new project from sales form
   */
  const createProject = useCallback(async (salesFormId, sidId = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/design-projects', {
        salesFormId,
        sidId
      });

      const newProject = response.data.designProject;

      setProjects(prev => [newProject, ...prev]);
      setSelectedProject(newProject);

      return newProject;
    } catch (err) {
      setError(err.message || 'Failed to create project');
      console.error('Error creating project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Assign SID to project
   */
  const assignSid = useCallback(async (projectId, sidId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patch(`/design-projects/${projectId}/assign-sid`, {
        sidId
      });

      const updatedProject = response.data.designProject;

      // Update in state
      setProjects(prev => prev.map(p =>
        p.id === projectId ? updatedProject : p
      ));

      if (selectedProject?.id === projectId) {
        setSelectedProject(updatedProject);
      }

      return updatedProject;
    } catch (err) {
      setError(err.message || 'Failed to assign SID');
      console.error('Error assigning SID:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  /**
   * Update workflow status
   */
  const updateWorkflowStatus = useCallback(async (projectId, newStatus, reason = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patch(`/design-projects/${projectId}/workflow-status`, {
        newStatus,
        reason
      });

      const updatedProject = response.data.data;

      // Update in state
      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, ...updatedProject } : p
      ));

      if (selectedProject?.id === projectId) {
        setSelectedProject(prev => ({ ...prev, ...updatedProject }));
      }

      return updatedProject;
    } catch (err) {
      setError(err.message || 'Failed to update workflow status');
      console.error('Error updating workflow status:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  /**
   * Approve 2D design
   */
  const approve2D = useCallback(async (projectId, notes = null) => {
    setLoading(true);
    setError(null);

    try {
      await api.post(`/design-projects/${projectId}/approve-2d`, { notes });

      // Refresh project to get updated status
      await fetchProjectById(projectId);
    } catch (err) {
      setError(err.message || 'Failed to approve 2D design');
      console.error('Error approving 2D:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProjectById]);

  /**
   * Approve 3D design
   */
  const approve3D = useCallback(async (projectId, notes = null) => {
    setLoading(true);
    setError(null);

    try {
      await api.post(`/design-projects/${projectId}/approve-3d`, { notes });

      // Refresh project to get updated status
      await fetchProjectById(projectId);
    } catch (err) {
      setError(err.message || 'Failed to approve 3D design');
      console.error('Error approving 3D:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProjectById]);

  /**
   * Record review meeting
   */
  const recordReviewMeeting = useCallback(async (projectId, meetingData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/design-projects/${projectId}/review-meetings`, meetingData);

      // Refresh project
      await fetchProjectById(projectId);

      return response.data.data;
    } catch (err) {
      setError(err.message || 'Failed to record review meeting');
      console.error('Error recording meeting:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProjectById]);

  /**
   * Update package price
   */
  const updatePackagePrice = useCallback(async (projectId, newPrice, reason) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patch(`/design-projects/${projectId}/package-price`, {
        newPrice,
        reason
      });

      const updatedData = response.data.data;

      // Update in state
      setProjects(prev => prev.map(p =>
        p.id === projectId
          ? { ...p, currentPackagePrice: updatedData.newPrice }
          : p
      ));

      if (selectedProject?.id === projectId) {
        setSelectedProject(prev => ({
          ...prev,
          currentPackagePrice: updatedData.newPrice
        }));
      }

      return updatedData;
    } catch (err) {
      setError(err.message || 'Failed to update package price');
      console.error('Error updating price:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Clear filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      workflowStatus: '',
      currentPhase: '',
      sidId: ''
    });
  }, []);

  /**
   * Refresh current project
   */
  const refreshProject = useCallback(async () => {
    if (selectedProject?.id) {
      await fetchProjectById(selectedProject.id);
    }
  }, [selectedProject, fetchProjectById]);

  /**
   * Upload project file
   */
  const uploadProjectFile = useCallback(async (formData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/project-files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to upload file');
      console.error('Error uploading file:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch project files
   */
  const fetchProjectFiles = useCallback(async (projectId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/project-files/project/${projectId}`);
      return response.data.files || [];
    } catch (err) {
      setError(err.message || 'Failed to fetch project files');
      console.error('Error fetching project files:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete project file
   */
  const deleteProjectFile = useCallback(async (fileId) => {
    setLoading(true);
    setError(null);

    try {
      await api.delete(`/project-files/${fileId}`);
    } catch (err) {
      setError(err.message || 'Failed to delete file');
      console.error('Error deleting file:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request 2D amendment
   */
  const requestAmendment2D = useCallback(async (projectId, amendmentData) => {
    setLoading(true);
    setError(null);

    try {
      await api.patch(`/design-projects/${projectId}/workflow-status`, {
        newStatus: '2d_amendment_required',
        reason: amendmentData.reason || amendmentData.amendmentNotes
      });

      // Refresh project
      await fetchProjectById(projectId);
    } catch (err) {
      setError(err.message || 'Failed to request 2D amendment');
      console.error('Error requesting 2D amendment:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProjectById]);

  /**
   * Request 3D amendment
   */
  const requestAmendment3D = useCallback(async (projectId, amendmentData) => {
    setLoading(true);
    setError(null);

    try {
      await api.patch(`/design-projects/${projectId}/workflow-status`, {
        newStatus: '3d_amendment_required',
        reason: amendmentData.reason || amendmentData.amendmentNotes
      });

      // Refresh project
      await fetchProjectById(projectId);
    } catch (err) {
      setError(err.message || 'Failed to request 3D amendment');
      console.error('Error requesting 3D amendment:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProjectById]);

  /**
   * Request signing amendment (client negotiation)
   */
  const requestSigningAmendment = useCallback(async (projectId, amendmentData) => {
    setLoading(true);
    setError(null);

    try {
      await api.patch(`/design-projects/${projectId}/workflow-status`, {
        newStatus: 'signing_amendment_required',
        reason: amendmentData.reason || amendmentData.amendmentNotes
      });

      // Refresh project
      await fetchProjectById(projectId);
    } catch (err) {
      setError(err.message || 'Failed to request signing amendment');
      console.error('Error requesting signing amendment:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProjectById]);

  // Socket.IO real-time updates
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return;

    const handleProjectUpdate = (data) => {
      console.log('Project updated via socket:', data);

      // Update project in list
      setProjects(prev => prev.map(p =>
        p.id === data.projectId
          ? { ...p, workflowStatus: data.newStatus }
          : p
      ));

      // Update selected project if it's the one that changed
      if (selectedProject?.id === data.projectId) {
        setSelectedProject(prev => ({
          ...prev,
          workflowStatus: data.newStatus
        }));
      }
    };

    const handleProjectCreated = (data) => {
      console.log('New project created:', data);
      // Could auto-refresh or show notification
    };

    socket.on('project_status_updated', handleProjectUpdate);
    socket.on('project:created', handleProjectCreated);
    socket.on('project_won', handleProjectUpdate);
    socket.on('project_lost', handleProjectUpdate);

    return () => {
      socket.off('project_status_updated', handleProjectUpdate);
      socket.off('project:created', handleProjectCreated);
      socket.off('project_won', handleProjectUpdate);
      socket.off('project_lost', handleProjectUpdate);
    };
  }, [socket, selectedProject]);

  const value = {
    // State
    projects,
    selectedProject,
    loading,
    error,
    filters,
    pagination,

    // Actions
    fetchProjects,
    fetchMyProjects,
    fetchProjectById,
    createProject,
    assignSid,
    updateWorkflowStatus,
    approve2D,
    approve3D,
    recordReviewMeeting,
    updatePackagePrice,
    updateFilters,
    clearFilters,
    refreshProject,
    setSelectedProject,

    // File management
    uploadProjectFile,
    fetchProjectFiles,
    deleteProjectFile,

    // Amendment requests
    requestAmendment2D,
    requestAmendment3D,
    requestSigningAmendment
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export default ProjectContext;
